import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
    Plane, 
    Navigation, 
    Gauge, 
    Mountain, 
    Clock, 
    MapPin, 
    ArrowUp,
    ArrowDown,
    Minus,
    X,
    Globe,
    Tag,
    Info,
    AlertCircle,
    ExternalLink,
    ShieldCheck
} from 'lucide-react';

const AIRHELP_AFFILIATE_URL = 'https://airhelp.tpx.gr/qaSrWHll';

// Airline code to name mapping for better display
const AIRLINE_NAMES = {
    // US Airlines
    'AAL': 'American Airlines', 'AA': 'American Airlines',
    'UAL': 'United Airlines', 'UA': 'United Airlines',
    'DAL': 'Delta Air Lines', 'DL': 'Delta Air Lines',
    'SWA': 'Southwest Airlines', 'WN': 'Southwest Airlines',
    'JBU': 'JetBlue Airways', 'B6': 'JetBlue Airways',
    'ASA': 'Alaska Airlines', 'AS': 'Alaska Airlines',
    'NKS': 'Spirit Airlines', 'NK': 'Spirit Airlines',
    'FFT': 'Frontier Airlines', 'F9': 'Frontier Airlines',
    // European Airlines
    'BAW': 'British Airways', 'BA': 'British Airways',
    'DLH': 'Lufthansa', 'LH': 'Lufthansa',
    'AFR': 'Air France', 'AF': 'Air France',
    'KLM': 'KLM Royal Dutch', 'KL': 'KLM Royal Dutch',
    'EZY': 'easyJet', 'U2': 'easyJet',
    'RYR': 'Ryanair', 'FR': 'Ryanair',
    'VIR': 'Virgin Atlantic', 'VS': 'Virgin Atlantic',
    'IBE': 'Iberia', 'IB': 'Iberia',
    'SAS': 'SAS Scandinavian', 'SK': 'SAS Scandinavian',
    // Middle East
    'UAE': 'Emirates', 'EK': 'Emirates',
    'ETD': 'Etihad Airways', 'EY': 'Etihad Airways',
    'QTR': 'Qatar Airways', 'QR': 'Qatar Airways',
    // Asia-Pacific
    'SIA': 'Singapore Airlines', 'SQ': 'Singapore Airlines',
    'CPA': 'Cathay Pacific', 'CX': 'Cathay Pacific',
    'QFA': 'Qantas', 'QF': 'Qantas',
    'JST': 'Jetstar', 'JQ': 'Jetstar',
    'VOZ': 'Virgin Australia', 'VA': 'Virgin Australia',
    'ANA': 'All Nippon Airways', 'NH': 'All Nippon Airways',
    'JAL': 'Japan Airlines', 'JL': 'Japan Airlines',
    'KAL': 'Korean Air', 'KE': 'Korean Air',
    'CAL': 'China Airlines', 'CI': 'China Airlines',
    'EVA': 'EVA Air', 'BR': 'EVA Air',
    'THA': 'Thai Airways', 'TG': 'Thai Airways',
    'MAS': 'Malaysia Airlines', 'MH': 'Malaysia Airlines',
    'ANZ': 'Air New Zealand', 'NZ': 'Air New Zealand',
    // Canada
    'ACA': 'Air Canada', 'AC': 'Air Canada',
    'WJA': 'WestJet', 'WS': 'WestJet',
};

// Australian airport codes for codeshare detection
const AUSTRALIAN_AIRPORTS = ['SYD', 'MEL', 'BNE', 'PER', 'ADL', 'OOL', 'CNS', 'DRW', 'CBR', 'HBA', 'TSV', 'NTL', 'AVV', 'MCY', 'LST'];

// New Zealand airport codes
const NZ_AIRPORTS = ['AKL', 'WLG', 'CHC', 'ZQN', 'DUD', 'NPE', 'PMR', 'NSN', 'HLZ', 'ROT', 'NPL', 'TRG', 'IVC'];

// Japan airport codes
const JAPAN_AIRPORTS = ['NRT', 'HND', 'KIX', 'ITM', 'NGO', 'CTS', 'FUK', 'OKA', 'SDJ', 'HIJ'];

// UK airport codes
const UK_AIRPORTS = ['LHR', 'LGW', 'STN', 'LTN', 'MAN', 'BHX', 'EDI', 'GLA', 'BRS', 'NCL', 'LPL', 'BFS'];

// Germany airport codes
const GERMANY_AIRPORTS = ['FRA', 'MUC', 'DUS', 'TXL', 'BER', 'HAM', 'CGN', 'STR', 'HAJ', 'NUE'];

// France airport codes  
const FRANCE_AIRPORTS = ['CDG', 'ORY', 'NCE', 'LYS', 'MRS', 'TLS', 'BOD', 'NTE'];

// Hong Kong
const HK_AIRPORTS = ['HKG'];

// Korea airport codes
const KOREA_AIRPORTS = ['ICN', 'GMP', 'PUS', 'CJU', 'TAE'];

// Netherlands airport codes
const NETHERLANDS_AIRPORTS = ['AMS', 'EIN', 'RTM'];

// Codeshare partnership mapping
const CODESHARE_PARTNERSHIPS = {
    'AA': {
        'australia': 'Qantas',
        'new_zealand': 'Qantas',
        'japan': 'Japan Airlines',
        'uk': 'British Airways',
        'hong_kong': 'Cathay Pacific',
        'default': 'Partner Airline'
    },
    'AAL': {
        'australia': 'Qantas',
        'new_zealand': 'Qantas', 
        'japan': 'Japan Airlines',
        'uk': 'British Airways',
        'hong_kong': 'Cathay Pacific',
        'default': 'Partner Airline'
    },
    'UA': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Air New Zealand',
        'japan': 'All Nippon Airways',
        'germany': 'Lufthansa',
        'default': 'Partner Airline'
    },
    'UAL': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Air New Zealand',
        'japan': 'All Nippon Airways',
        'germany': 'Lufthansa',
        'default': 'Partner Airline'
    },
    'DL': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Virgin Australia',
        'korea': 'Korean Air',
        'france': 'Air France',
        'netherlands': 'KLM',
        'default': 'Partner Airline'
    },
    'DAL': {
        'australia': 'Virgin Australia',
        'new_zealand': 'Virgin Australia',
        'korea': 'Korean Air',
        'france': 'Air France',
        'netherlands': 'KLM',
        'default': 'Partner Airline'
    }
};

// Determine the region based on airport codes
const getRegionFromAirports = (departureIata, arrivalIata) => {
    const dep = departureIata?.toUpperCase();
    const arr = arrivalIata?.toUpperCase();
    
    if ((dep && AUSTRALIAN_AIRPORTS.includes(dep)) || (arr && AUSTRALIAN_AIRPORTS.includes(arr))) {
        if ((AUSTRALIAN_AIRPORTS.includes(dep) && AUSTRALIAN_AIRPORTS.includes(arr)) ||
            (AUSTRALIAN_AIRPORTS.includes(dep) && NZ_AIRPORTS.includes(arr)) ||
            (NZ_AIRPORTS.includes(dep) && AUSTRALIAN_AIRPORTS.includes(arr))) {
            return 'australia';
        }
        if (AUSTRALIAN_AIRPORTS.includes(dep) || AUSTRALIAN_AIRPORTS.includes(arr)) {
            return 'australia';
        }
    }
    
    if ((dep && NZ_AIRPORTS.includes(dep)) && (arr && NZ_AIRPORTS.includes(arr))) {
        return 'new_zealand';
    }
    if ((dep && NZ_AIRPORTS.includes(dep)) || (arr && NZ_AIRPORTS.includes(arr))) {
        return 'new_zealand';
    }
    
    if ((dep && JAPAN_AIRPORTS.includes(dep)) || (arr && JAPAN_AIRPORTS.includes(arr))) {
        return 'japan';
    }
    
    if ((dep && UK_AIRPORTS.includes(dep)) || (arr && UK_AIRPORTS.includes(arr))) {
        return 'uk';
    }
    
    if ((dep && GERMANY_AIRPORTS.includes(dep)) || (arr && GERMANY_AIRPORTS.includes(arr))) {
        return 'germany';
    }
    
    if ((dep && FRANCE_AIRPORTS.includes(dep)) || (arr && FRANCE_AIRPORTS.includes(arr))) {
        return 'france';
    }
    
    if ((dep && HK_AIRPORTS.includes(dep)) || (arr && HK_AIRPORTS.includes(arr))) {
        return 'hong_kong';
    }
    
    if ((dep && KOREA_AIRPORTS.includes(dep)) || (arr && KOREA_AIRPORTS.includes(arr))) {
        return 'korea';
    }
    
    if ((dep && NETHERLANDS_AIRPORTS.includes(dep)) || (arr && NETHERLANDS_AIRPORTS.includes(arr))) {
        return 'netherlands';
    }
    
    return null;
};

// Get the actual operating carrier for a codeshare flight
const getOperatingCarrier = (callsign, departureIata, arrivalIata) => {
    if (!callsign) return null;
    
    const cleaned = callsign.trim().toUpperCase();
    const prefix3 = cleaned.substring(0, 3);
    const prefix2 = cleaned.substring(0, 2);
    
    const carrierCode = CODESHARE_PARTNERSHIPS[prefix3] ? prefix3 : 
                        CODESHARE_PARTNERSHIPS[prefix2] ? prefix2 : null;
    
    if (!carrierCode) return null;
    
    const region = getRegionFromAirports(departureIata, arrivalIata);
    
    if (!region) return null;
    
    const partnerships = CODESHARE_PARTNERSHIPS[carrierCode];
    return partnerships[region] || partnerships['default'];
};

// Check if a flight is likely a codeshare
const isLikelyCodeshare = (callsign, originCountry, apiAirlineName, departureIata, arrivalIata) => {
    const usAirlines = ['American Airlines', 'United Airlines', 'Delta Air Lines', 'Delta'];
    const usCallsignPrefixes = ['AAL', 'AA', 'UAL', 'UA', 'DAL', 'DL'];
    
    let isUSAirline = false;
    if (apiAirlineName && usAirlines.some(us => apiAirlineName.includes(us))) {
        isUSAirline = true;
    }
    if (callsign) {
        const cleaned = callsign.trim().toUpperCase();
        const prefix3 = cleaned.substring(0, 3);
        const prefix2 = cleaned.substring(0, 2);
        if (usCallsignPrefixes.includes(prefix3) || usCallsignPrefixes.includes(prefix2)) {
            isUSAirline = true;
        }
    }
    
    if (!isUSAirline) return { isCodeshare: false, operatingCarrier: null };
    
    const operatingCarrier = getOperatingCarrier(callsign, departureIata, arrivalIata);
    
    if (operatingCarrier) {
        return { isCodeshare: true, operatingCarrier };
    }
    
    const nonUSCountries = ['Australia', 'New Zealand', 'Japan', 'China', 'Germany', 'France', 'United Kingdom', 'Brazil', 'India', 'South Korea', 'Hong Kong', 'Netherlands'];
    if (nonUSCountries.includes(originCountry)) {
        return { isCodeshare: true, operatingCarrier: 'Partner Airline' };
    }
    
    return { isCodeshare: false, operatingCarrier: null };
};

// Parse callsign - handle codeshares with smart mapping
const parseCallsign = (callsign, originCountry, apiAirlineName, departureIata, arrivalIata) => {
    if (!callsign) return { airline: null, flightNumber: null, raw: 'Unknown', isCodeshare: false, operatingCarrier: null };
    
    const cleaned = callsign.trim().toUpperCase();
    const codeshareInfo = isLikelyCodeshare(callsign, originCountry, apiAirlineName, departureIata, arrivalIata);
    
    // If it's a codeshare, show the operating carrier
    if (codeshareInfo.isCodeshare && codeshareInfo.operatingCarrier) {
        const flightNum = cleaned.replace(/^[A-Z]{2,3}/, '');
        return {
            airline: codeshareInfo.operatingCarrier,
            flightNumber: flightNum || cleaned,
            raw: cleaned,
            isCodeshare: true,
            operatingCarrier: codeshareInfo.operatingCarrier
        };
    }
    
    // If API provided an airline name, use it
    if (apiAirlineName) {
        const flightNum = cleaned.replace(/^[A-Z]{2,3}/, '');
        return {
            airline: apiAirlineName,
            flightNumber: flightNum || null,
            raw: cleaned,
            isCodeshare: false,
            operatingCarrier: null
        };
    }
    
    // Try 3-letter ICAO code first
    const icaoPrefix = cleaned.substring(0, 3);
    if (AIRLINE_NAMES[icaoPrefix]) {
        const flightNum = cleaned.substring(3);
        return {
            airline: AIRLINE_NAMES[icaoPrefix],
            flightNumber: flightNum || null,
            raw: cleaned,
            isCodeshare: false,
            operatingCarrier: null
        };
    }
    
    // Try 2-letter IATA code
    const iataPrefix = cleaned.substring(0, 2);
    if (AIRLINE_NAMES[iataPrefix]) {
        const flightNum = cleaned.substring(2);
        return {
            airline: AIRLINE_NAMES[iataPrefix],
            flightNumber: flightNum || null,
            raw: cleaned,
            isCodeshare: false,
            operatingCarrier: null
        };
    }
    
    return { airline: null, flightNumber: null, raw: cleaned, isCodeshare: false, operatingCarrier: null };
};

export const FlightDetails = ({ flight, onClose }) => {
    const { user } = useAuth();
    const isPremium = user?.is_premium === true;
    
    if (!flight) return null;

    // Simplify airport names (remove "International" suffix for cleaner display)
    const simplifyAirportName = (airportName) => {
        if (!airportName) return 'Unknown';
        return airportName
            .replace(/\s+International\s*(Airport)?$/i, '')
            .replace(/\s+Airport$/i, '')
            .replace(/\s+Regional$/i, '')
            .trim();
    };

    const formatTimestamp = (unix) => {
        if (!unix) return 'N/A';
        return new Date(unix * 1000).toLocaleTimeString();
    };

    const formatCoordinate = (value, isLat) => {
        if (value === null || value === undefined) return 'N/A';
        const dir = isLat 
            ? (value >= 0 ? 'N' : 'S') 
            : (value >= 0 ? 'E' : 'W');
        return `${Math.abs(value).toFixed(4)}° ${dir}`;
    };

    const getVerticalStatus = () => {
        const rate = flight.vertical_rate;
        if (!rate || Math.abs(rate) < 1) {
            return { icon: Minus, text: 'Level', color: 'text-[#94a3b8]' };
        }
        if (rate > 0) {
            return { icon: ArrowUp, text: 'Climbing', color: 'text-green-500' };
        }
        return { icon: ArrowDown, text: 'Descending', color: 'text-yellow-500' };
    };

    const verticalStatus = getVerticalStatus();
    const VerticalIcon = verticalStatus.icon;
    
    // Parse the callsign for better display - pass all relevant fields to detect codeshares
    const parsedCallsign = parseCallsign(
        flight.callsign, 
        flight.origin_country, 
        flight.airline_name,
        flight.departure_iata,
        flight.arrival_iata
    );

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
                className="glass-card rounded-xl overflow-hidden max-h-[500px] flex flex-col"
            >
                <Card className="bg-transparent border-none flex flex-col flex-1 min-h-0">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#f07050]/20 rounded-lg">
                                <Plane className="h-6 w-6 text-[#f07050]" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <CardTitle className="text-xl font-bold text-white">
                                        {parsedCallsign.airline || flight.callsign || flight.icao24}
                                    </CardTitle>
                                    {parsedCallsign.isCodeshare && (
                                        <span className="px-1.5 py-0.5 text-[10px] bg-amber-500/20 text-amber-400 rounded font-medium">
                                            OPERATED
                                        </span>
                                    )}
                                </div>
                                {parsedCallsign.flightNumber && (
                                    <p className="text-sm text-[#f07050] font-mono font-bold">
                                        Flight {parsedCallsign.raw}
                                    </p>
                                )}
                                <p className="text-sm text-[#94a3b8]">
                                    {flight.origin_country || 'Unknown Origin'}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="text-[#64748b] hover:text-white hover:bg-[#334155]"
                            data-testid="close-flight-details"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </CardHeader>

                    <CardContent className="space-y-4 overflow-y-auto flex-1 min-h-0">
                        {/* Route Info - Show prominently at top for premium data */}
                        {flight.is_premium_data && (flight.departure_iata || flight.arrival_iata) && (
                            <div className="bg-gradient-to-r from-[#f07050]/20 to-[#f07050]/5 rounded-lg p-4 border border-[#f07050]/30">
                                <div className="flex items-center justify-between">
                                    <div className="text-center flex-1">
                                        <p className="text-2xl font-bold text-white">{flight.departure_iata || '---'}</p>
                                        <p className="text-xs text-[#94a3b8]">{flight.departure_airport || 'Departure'}</p>
                                    </div>
                                    <div className="flex items-center gap-2 px-4">
                                        <div className="h-[2px] w-8 bg-[#f07050]/50"></div>
                                        <Plane className="h-5 w-5 text-[#f07050] rotate-90" />
                                        <div className="h-[2px] w-8 bg-[#f07050]/50"></div>
                                    </div>
                                    <div className="text-center flex-1">
                                        <p className="text-2xl font-bold text-white">{flight.arrival_iata || '---'}</p>
                                        <p className="text-xs text-[#94a3b8]">{flight.arrival_airport || 'Arrival'}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Status Badge */}
                        <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                                flight.on_ground 
                                    ? 'bg-yellow-500/20 text-yellow-500' 
                                    : 'bg-green-500/20 text-green-500'
                            }`}>
                                <div className="w-2 h-2 rounded-full bg-current animate-pulse"></div>
                                <span className="text-xs font-medium">
                                    {flight.on_ground ? 'On Ground' : 'In Flight'}
                                </span>
                            </div>
                            <div className={`flex items-center gap-1 px-3 py-1 rounded-full bg-[#334155] ${verticalStatus.color}`}>
                                <VerticalIcon className="h-3 w-3" />
                                <span className="text-xs font-medium">{verticalStatus.text}</span>
                            </div>
                        </div>

                        {/* Main Stats Grid */}
                        <div className="grid grid-cols-2 gap-3">
                            {/* Altitude */}
                            <div className="bg-[#071D35]/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Mountain className="h-4 w-4 text-[#64748b]" />
                                    <span className="data-label">Altitude</span>
                                </div>
                                <p className="data-value text-lg">
                                    {flight.altitude_feet?.toLocaleString() || '---'}
                                    <span className="text-xs text-[#64748b] ml-1">ft</span>
                                </p>
                            </div>

                            {/* Speed */}
                            <div className="bg-[#071D35]/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Gauge className="h-4 w-4 text-[#64748b]" />
                                    <span className="data-label">Speed</span>
                                </div>
                                <p className="data-value text-lg">
                                    {flight.velocity_knots?.toFixed(0) || '---'}
                                    <span className="text-xs text-[#64748b] ml-1">kts</span>
                                </p>
                            </div>

                            {/* Heading */}
                            <div className="bg-[#071D35]/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <Navigation className="h-4 w-4 text-[#64748b]" />
                                    <span className="data-label">Heading</span>
                                </div>
                                <p className="data-value text-lg">
                                    {flight.heading?.toFixed(0) || '---'}
                                    <span className="text-xs text-[#64748b] ml-1">°</span>
                                </p>
                            </div>

                            {/* Vertical Rate */}
                            <div className="bg-[#071D35]/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-1">
                                    <ArrowUp className="h-4 w-4 text-[#64748b]" />
                                    <span className="data-label">V/S Rate</span>
                                </div>
                                <p className={`data-value text-lg ${verticalStatus.color}`}>
                                    {flight.vertical_rate?.toFixed(1) || '---'}
                                    <span className="text-xs text-[#64748b] ml-1">m/s</span>
                                </p>
                            </div>
                        </div>

                        {/* Position */}
                        <div className="bg-[#071D35]/50 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <MapPin className="h-4 w-4 text-[#64748b]" />
                                <span className="data-label">Position</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                                <div>
                                    <span className="text-[#64748b]">LAT: </span>
                                    <span className="text-white">
                                        {formatCoordinate(flight.latitude, true)}
                                    </span>
                                </div>
                                <div>
                                    <span className="text-[#64748b]">LON: </span>
                                    <span className="text-white">
                                        {formatCoordinate(flight.longitude, false)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Aircraft Info */}
                        <div className="bg-[#071D35]/50 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <Info className="h-4 w-4 text-[#64748b]" />
                                <span className="data-label">Aircraft Info</span>
                            </div>
                            <div className="space-y-1 text-sm">
                                {parsedCallsign.airline && (
                                    <div className="flex justify-between">
                                        <span className="text-[#64748b]">Airline:</span>
                                        <span className="text-white">{parsedCallsign.airline}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-[#64748b]">Callsign:</span>
                                    <span className="font-mono text-[#f07050] font-bold">{parsedCallsign.raw}</span>
                                </div>
                                {flight.aircraft_type && (
                                    <div className="flex justify-between">
                                        <span className="text-[#64748b]">Aircraft:</span>
                                        <span className="font-mono text-white">{flight.aircraft_type}</span>
                                    </div>
                                )}
                                {flight.registration && (
                                    <div className="flex justify-between">
                                        <span className="text-[#64748b]">Registration:</span>
                                        <span className="font-mono text-white">{flight.registration}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-[#64748b]">ICAO24:</span>
                                    <span className="font-mono text-[#94a3b8]">{flight.icao24}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-[#64748b]">Country:</span>
                                    <span className="text-white">{flight.origin_country || 'Unknown'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Route Info Note */}
                        {flight.is_premium_data ? (
                            <div className="bg-[#071D35]/50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Globe className="h-4 w-4 text-[#f07050]" />
                                    <span className="data-label text-[#f07050]">Route Details</span>
                                </div>
                                {(flight.departure_iata || flight.arrival_iata) ? (
                                    <div className="space-y-2 text-sm">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[#64748b]">From:</span>
                                            <span className="text-white font-medium">
                                                {simplifyAirportName(flight.departure_airport)}
                                                {flight.departure_iata && ` (${flight.departure_iata})`}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[#64748b]">To:</span>
                                            <span className="text-white font-medium">
                                                {simplifyAirportName(flight.arrival_airport)}
                                                {flight.arrival_iata && ` (${flight.arrival_iata})`}
                                            </span>
                                        </div>
                                        {flight.departure_scheduled && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-[#64748b]">Departure:</span>
                                                <span className="text-white">{new Date(flight.departure_scheduled).toLocaleString()}</span>
                                            </div>
                                        )}
                                        {flight.arrival_estimated && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-[#64748b]">ETA:</span>
                                                <span className="text-white">{new Date(flight.arrival_estimated).toLocaleString()}</span>
                                            </div>
                                        )}
                                        {flight.flight_status && (
                                            <div className="flex items-center justify-between">
                                                <span className="text-[#64748b]">Status:</span>
                                                <span className="text-green-400 capitalize">{flight.flight_status}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-xs text-[#64748b]">
                                        Route data not available for this flight from the data provider.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="bg-[#334155]/30 rounded-lg p-3 border border-[#334155]">
                                <div className="flex items-start gap-2">
                                    <Globe className="h-4 w-4 text-[#64748b] mt-0.5" />
                                    <p className="text-xs text-[#64748b]">
                                        <span className="text-[#94a3b8]">Route data (departure/destination)</span> is available for Premium subscribers.
                                        <span className="text-[#f07050] ml-1">Upgrade to see full flight routes!</span>
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* AirHelp Compensation Widget - Show for non-premium users */}
                        {!isPremium && (
                            <a 
                                href={AIRHELP_AFFILIATE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                                data-testid="airhelp-compensation-widget"
                            >
                                <div className="bg-gradient-to-r from-blue-900/40 to-blue-800/20 rounded-lg p-3 border border-blue-700/30 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10 group">
                                    <div className="flex items-start gap-3">
                                        {/* Icon */}
                                        <div className="flex-shrink-0 w-10 h-10 bg-blue-600/30 rounded-lg flex items-center justify-center group-hover:bg-blue-600/50 transition-colors">
                                            <ShieldCheck className="h-5 w-5 text-blue-400" />
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="text-sm font-semibold text-white">Flight Delayed or Cancelled?</p>
                                                <ExternalLink className="h-3 w-3 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                            <p className="text-xs text-blue-200/80 mt-0.5">
                                                Claim up to <span className="text-green-400 font-bold">€600</span> compensation
                                            </p>
                                            
                                            {/* Quick Benefits */}
                                            <div className="flex items-center gap-3 mt-2 text-xs text-blue-300/70">
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                    </svg>
                                                    No win, no fee
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                                    </svg>
                                                    3 min check
                                                </span>
                                            </div>
                                        </div>
                                        
                                        {/* CTA Arrow */}
                                        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 group-hover:bg-blue-500 transition-colors">
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        )}

                        {/* Last Update */}
                        <div className="flex items-center justify-center gap-2 text-xs text-[#64748b]">
                            <Clock className="h-3 w-3" />
                            <span>Last contact: {formatTimestamp(flight.last_contact)}</span>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </AnimatePresence>
    );
};
